import { createFeatureSelector } from '@ngrx/store';
import { createSelector } from 'reselect';
import * as fromDatasetResult from './dataset-result.reducer';
import { DataViewTypes, FeatureFlag, ProducerTypes } from '@mode/shared/contract-common';
import { getInitialFlagValue } from '@mode/shared/util-js';

const isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

export const selectDatasetResultState = createFeatureSelector<fromDatasetResult.DatasetResultFeatureState>(
  fromDatasetResult.datasetResultFeatureKey
);

export const getDatasetResults = createSelector(selectDatasetResultState, fromDatasetResult.selectAll);

export const getDatasetResultEntities = createSelector(selectDatasetResultState, fromDatasetResult.selectEntities);

export const getDatasetResultByToken = (switchViewToken: string) =>
  createSelector(getDatasetResultEntities, (entities) => entities[switchViewToken]);

export const getSuccessfulDatasets = createSelector(getDatasetResults, (datasetResults) =>
  datasetResults.filter((datasetResult) => datasetResult.queryRunState === DataViewTypes.QueryRunStatus.Succeeded)
);

export const getDatasetWithRemovalFail = createSelector(
  getDatasetResults,
  (datasetResults) => datasetResults.filter((datasetResult) => datasetResult.error !== null)[0]
);

export const getDatasetWithOneSwitchView = createSelector(getDatasetResults, (datasetResults) =>
  isExportDatasetsEnabled
    ? datasetResults.filter((dataset) => dataset.producerType !== ProducerTypes.NotebookCell).length > 1
    : datasetResults.length > 1
);

export const getDatasetsLimitExceeded = createSelector(
  getDatasetResults,
  (datasetResults) =>
    datasetResults.filter((dataset) => dataset.producerType === ProducerTypes.NotebookCell).length > 15
);

export const getDatasetResultCompletedAtBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.completedAt);

export const getBytesSizeBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.bytes);

export const getRawQueryBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.rawQuery);

export const getRenderedQueryByViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.renderedQuery);

export const getRowCountBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.rowCount);

export const getCsvUrlBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.csvUrl);

export const getLegacyContentBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.legacyContent);

export const getMetaColumnsBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.metaColumns);

export const getQueryRunStateBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.queryRunState);

export const getJsonUrlBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.jsonUrl);

// @TODO: (@anthonysimone) [GF] this is only temporary until the selectors below are refactored.
// This should be removed afterward.
export const getDatasetResultByQueryToken = (queryToken: string) => {
  return createSelector(getDatasetResults, (datasetResults) => {
    return datasetResults.find((datasetResult) => datasetResult.queryToken === queryToken);
  });
};

export const getDatasetResultBySwitchViewToken = (switchViewToken: string) => {
  return createSelector(getDatasetResults, (datasetResults) =>
    datasetResults.find((datasetResult) => datasetResult.switchViewToken === switchViewToken)
  );
};

export const getPublishDatasetResultBySwitchViewToken = (switchViewToken: string) => {
  return createSelector(
    getDatasetResultBySwitchViewToken(switchViewToken),
    (datasetResult) => datasetResult?.thoughtspot
  );
};

export const isPublishedDatasetBySwitchViewToken = (switchViewToken: string) => {
  return createSelector(
    getPublishDatasetResultBySwitchViewToken(switchViewToken),
    (thoughtspot) => !!thoughtspot?.publishedAt
  );
};
export const getDatasetResultByProducerToken = (producerToken: string) => {
  return createSelector(getDatasetResults, (datasetResults) => {
    return datasetResults.find((datasetResult) => datasetResult.producerToken === producerToken);
  });
};

// @TODO: (@anthonysimone) [GF] This is temporary until the selectors can be refactored to use the SwitchViewToken
// Should be removed afterward
export const getDatasetResultByQueryRunToken = (queryRunToken: string) => {
  return createSelector(getDatasetResults, (datasetResults) => {
    return datasetResults.find((datasetResult) => datasetResult.queryRunToken === queryRunToken);
  });
};

export const getRawQueryByQueryToken = (queryToken: string) =>
  createSelector(getDatasetResultByQueryToken(queryToken), (entity) => entity && entity.rawQuery);

export const getRenderedQueryByQueryToken = (queryToken: string) =>
  createSelector(getDatasetResultByQueryToken(queryToken), (entity) => entity && entity.renderedQuery);

export const getErrorByQueryToken = (queryToken: string) =>
  createSelector(getDatasetResultByQueryToken(queryToken), (entity) => entity && entity.error);

export const getQueryTokenBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.queryToken);

export const getSwitchViewTokenByQueryToken = (queryToken: string) =>
  createSelector(getDatasetResultByQueryToken(queryToken), (datasetResult) => datasetResult?.switchViewToken);

export const getQueryNameBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getDatasetResultByToken(switchViewToken), (entity) => entity && entity.queryName);

// Get a specific dataView by query_token
// @TODO: (@anthonysimone) [GF] This will also be a problem in the future wrt the name of a switchView being
// the queryToken, as it will not be guaranteed to be unique in the future. We know we need
// to adjust this, but calling it out here. This will need to be updated to be keyed
// off of switchViewToken, and the other code adjusted as well once the BE
// code is refactored, but must be done before the current GF milestone finished.
export const createQueryRunTokenSelectorByQueryToken = (queryToken: string) => {
  return createSelector(getDatasetResultByQueryToken(queryToken), (datasetResult) => {
    return datasetResult?.queryRunToken;
  });
};

// Get a specific runState by query_token
// @TODO: (@anthonysimone) [GF] This will also be a problem in the future wrt the name of a switchView being
// the queryToken, as it will not be guaranteed to be unique in the future. We know we need
// to adjust this, but calling it out here. This will need to be updated to be keyed
// off of switchViewToken, and the other code adjusted as well once the BE
// code is refactored, but must be done before the current GF milestone finished.
export const getQueryRunStateByQueryToken = (queryToken: string) => {
  return createSelector(getDatasetResultByQueryToken(queryToken), (datasetResult) => {
    return datasetResult?.queryRunState;
  });
};
